import { Mail, Award, GraduationCap, Users, BookOpen, Globe, FileText, HeartPulse, Shield, Clock } from 'lucide-react';

export function Team() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Leadership Team</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Our experienced team brings together decades of expertise in neurology and healthcare innovation.
        </p>
      </div>

      <div className="mb-16">
        <div className="bg-white rounded-lg overflow-hidden shadow-md">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Jorge L. Juncos, MD</h2>
            <p className="text-blue-600 font-semibold mb-4">Medical Director & Senior Neurologist</p>
            <div className="space-y-3 mb-6">
              <div className="flex items-center">
                <GraduationCap className="h-5 w-5 text-gray-500 mr-2" />
                <p className="text-gray-600">MD, Columbia University College of Physicians and Surgeons</p>
              </div>
              <div className="flex items-center">
                <Award className="h-5 w-5 text-gray-500 mr-2" />
                <p className="text-gray-600">Board Certified in Neurology</p>
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 text-gray-500 mr-2" />
                <p className="text-gray-600">Fellowship in Movement Disorders, National Health Institute (NIH)</p>
              </div>
              <div className="flex items-center">
                <BookOpen className="h-5 w-5 text-gray-500 mr-2" />
                <p className="text-gray-600">Over 112 Peer-Reviewed Publications</p>
              </div>
              <div className="flex items-center">
                <FileText className="h-5 w-5 text-gray-500 mr-2" />
                <p className="text-gray-600">Principal Investigator on Multiple NIH-Funded Studies</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Expertise</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Movement Disorders</li>
                  <li>• Parkinson's Disease</li>
                  <li>• Deep Brain Stimulation Management</li>
                  <li>• Neurodegenerative Disorders</li>
                  <li>• Complex Neurological Conditions</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Academic Achievements</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Former Professor of Neurology at Emory University</li>
                  <li>• Director of Movement Disorders Program (1988-2022)</li>
                  <li>• Multiple Research Grants from NIH</li>
                  <li>• Numerous Clinical Trial Publications</li>
                  <li>• International Speaker and Educator</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Professional Background</h3>
              <p className="text-gray-600 mb-4">
                Dr. Juncos brings over 45 years of distinguished experience in neurology, with particular expertise in 
                movement disorders and neurodegenerative diseases. As the former Professor of Neurology and Director 
                of the Movement Disorders Program at Emory University School of Medicine (1988-2022), he has pioneered 
                innovative treatment approaches and contributed significantly to the field through groundbreaking research.
              </p>
              <p className="text-gray-600 mb-4">
                His research has been continuously funded by the National Institutes of Health and numerous private 
                foundations, leading to transformative discoveries in Parkinson's disease and related disorders. 
                Dr. Juncos has been instrumental in developing and implementing advanced therapeutic protocols, 
                including specialized treatments for complex movement disorders and deep brain stimulation management.
              </p>
              <p className="text-gray-600">
                Throughout his career, Dr. Juncos has been recognized internationally for his contributions to 
                neurological care. He has served on multiple advisory boards, including the Parkinson's Foundation 
                and the Movement Disorder Society. His commitment to patient care combines cutting-edge research 
                with compassionate, personalized treatment approaches.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <a
                  href="mailto:clinic@juncosneuroconsulting.com"
                  className="flex items-center text-blue-600 hover:text-blue-700"
                >
                  <Mail className="h-5 w-5 mr-1" />
                  Contact Dr. Juncos
                </a>
              </div>
              <div className="flex items-center gap-4">
                <Globe className="h-5 w-5 text-gray-500" />
                <span className="text-gray-600">Accepting New Patients</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-16">
        <div className="bg-white rounded-lg overflow-hidden shadow-md">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">George Burton, MD, MPH, MS</h2>
            <p className="text-blue-600 font-semibold mb-4">Clinical Director & Innovation Lead</p>
            <div className="space-y-3 mb-6">
              <div className="flex items-center">
                <GraduationCap className="h-5 w-5 text-gray-500 mr-2" />
                <p className="text-gray-600">MD, Washington University, School of Health Sciences</p>
              </div>
              <div className="flex items-center">
                <GraduationCap className="h-5 w-5 text-gray-500 mr-2" />
                <p className="text-gray-600">MPH, Liberty University</p>
              </div>
              <div className="flex items-center">
                <GraduationCap className="h-5 w-5 text-gray-500 mr-2" />
                <p className="text-gray-600">MS, in Artificial Intelligence & Machine Learning, Colorado State University</p>
              </div>
              <div className="flex items-center">
                <GraduationCap className="h-5 w-5 text-gray-500 mr-2" />
                <p className="text-gray-600">MS, in Cyber Security and Computational Sciences, Liberty University</p>
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 text-gray-500 mr-2" />
                <p className="text-gray-600">United States Navy Veteran</p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Professional Background</h3>
              <p className="text-gray-600 mb-4">
                Dr. Burton serves as the Clinical Director & Innovation Lead at our clinic, where he plays a central role in guiding 
                patient care strategies, optimizing clinical operations, and thoughtfully integrating advanced technologies. While he 
                does not currently practice as a licensed physician, Dr. Burton holds a Doctor of Medicine degree and brings a wealth 
                of clinical knowledge, strategic leadership, and operational insight to the team.
              </p>
              <p className="text-gray-600 mb-4">
                A proud veteran of the United States Navy, Dr. Burton's background in military healthcare and operations has shaped 
                his unwavering commitment to excellence, efficiency, and patient-centered care. His service instilled in him the 
                discipline, leadership, and problem-solving skills that now inform both the day-to-day functions and long-term 
                vision of the clinic.
              </p>
              <p className="text-gray-600">
                With advanced training in medicine, public health, artificial intelligence, and cybersecurity, Dr. Burton leads 
                innovative initiatives aimed at improving patient outcomes, enhancing the care experience, and ensuring the safe 
                and responsible use of technology in healthcare. His unique combination of clinical expertise and technical acumen 
                helps us stay at the forefront of modern medicine—creating a compassionate, secure, and high-performing environment 
                for every patient we serve.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Areas of Focus</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Clinical Operations Management</li>
                  <li>• Healthcare Innovation</li>
                  <li>• Quality Improvement</li>
                  <li>• Technology Integration</li>
                  <li>• Patient Care Strategy</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Leadership Contributions</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Clinical Workflow Optimization</li>
                  <li>• Staff Development Programs</li>
                  <li>• Quality Metrics Implementation</li>
                  <li>• Patient Experience Enhancement</li>
                  <li>• Healthcare Technology Adoption</li>
                </ul>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <a
                  href="mailto:clinic@juncosneuroconsulting.com"
                  className="flex items-center text-blue-600 hover:text-blue-700"
                >
                  <Mail className="h-5 w-5 mr-1" />
                  Contact Dr. Burton
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Clinical Support</h2>
        <div className="grid md:grid-cols-1 gap-8">
          <div className="bg-white rounded-lg overflow-hidden shadow-md">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Mrs. Cherita Smith, CMA</h3>
              <p className="text-blue-600 font-semibold mb-4">Senior Patient Care Coordinator</p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center">
                  <Award className="h-5 w-5 text-gray-500 mr-2" />
                  <p className="text-gray-600">Certified Medical Assistant</p>
                </div>
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-gray-500 mr-2" />
                  <p className="text-gray-600">Extensive Experience in Neurological Care</p>
                </div>
                <div className="flex items-center">
                  <Shield className="h-5 w-5 text-gray-500 mr-2" />
                  <p className="text-gray-600">Advanced Patient Care Coordination Certification</p>
                </div>
                <div className="flex items-center">
                  <HeartPulse className="h-5 w-5 text-gray-500 mr-2" />
                  <p className="text-gray-600">Specialized Training in Neurological Patient Care</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Areas of Expertise</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Patient Care Coordination</li>
                    <li>• Insurance Authorization Management</li>
                    <li>• Clinical Operations</li>
                    <li>• Patient Advocacy</li>
                    <li>• Healthcare Documentation</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Responsibilities</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Patient Care Protocol Implementation</li>
                    <li>• Appointment Scheduling Optimization</li>
                    <li>• Insurance Coordination</li>
                    <li>• Patient Education</li>
                    <li>• Clinical Team Support</li>
                  </ul>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-6 mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Professional Background</h3>
                <p className="text-gray-600 mb-4">
                  As our Senior Patient Care Coordinator, Mrs. Smith brings extensive expertise in patient 
                  care coordination and clinical operations. She masterfully orchestrates complex insurance 
                  authorizations and seamless appointment scheduling while ensuring exceptional patient 
                  experiences. Her deep understanding of neurological care processes and unwavering 
                  commitment to patient advocacy ensures the highest quality of care delivery.
                </p>
                <p className="text-gray-600">
                  Mrs. Smith's leadership in implementing innovative patient care protocols has been 
                  instrumental in creating our welcoming, patient-centered environment. Her expertise 
                  in healthcare operations and dedication to patient care make her an invaluable member 
                  of our clinical team, ensuring smooth daily operations and outstanding patient service.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <a
                    href="mailto:clinic@juncosneuroconsulting.com"
                    className="flex items-center text-blue-600 hover:text-blue-700"
                  >
                    <Mail className="h-5 w-5 mr-1" />
                    Contact Mrs. Smith
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <Users className="h-5 w-5 text-gray-500" />
                  <span className="text-gray-600">Patient Care Specialist</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}